import { useMemo } from 'react'
import ReactApexChart from 'react-apexcharts'
import dayjs from 'dayjs'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Paper from '@mui/material/Paper'

const TechnicalGraphContainer = ({
  openTable,
  dynamicHeadings,
  mapInverterSeries,
  selectedButtons,
  yaxisUnit,
  inverterviewbutton,
  technicalToDate,
  technicalFromDate,
}) => {
  // Ensure mapInverterSeries is always defined
  const validMapInverterSeries = useMemo(() => {
    console.log('validMapInverterSeries input:', mapInverterSeries)
    return mapInverterSeries || []
  }, [mapInverterSeries])

  const hasDualAxes = useMemo(() => {
    const hasEnergyData = validMapInverterSeries.some((series) =>
      ['kWh', 'kW'].includes(series.unit)
    )
    const hasIrradianceData = validMapInverterSeries.some(
      (series) => series.unit === 'kW/m²'
    )
    return hasEnergyData && hasIrradianceData
  }, [validMapInverterSeries])

  // Since backend now sends pre-aggregated data, we just need to use it directly
  const processedData = useMemo(() => {
    console.log('Processing data for chart:', validMapInverterSeries)

    if (!validMapInverterSeries || validMapInverterSeries.length === 0) {
      console.log('No valid series data available')
      return []
    }

    const baseDate = dayjs(technicalFromDate).isValid()
      ? dayjs(technicalFromDate).startOf('day')
      : dayjs().startOf('day')

    // Backend already provides the correct aggregated data based on duration
    // We just need to format it for the chart
    return validMapInverterSeries.map((series) => {
      const validatedData = (series.data || [])
        .map((point) => {
          let date
          if (inverterviewbutton === 'day') {
  const timeStr = dayjs(point.x).format('HH:mm:ss')
  const timeMatch = timeStr.match(/^(\d{2}):(\d{2})(?::(\d{2}))?$/)
  
  if (timeMatch) {
    date = baseDate
      .set('hour', parseInt(timeMatch[1]))
      .set('minute', parseInt(timeMatch[2]))
    if (timeMatch[3]) {
      date = date.set('second', parseInt(timeMatch[3]))
    }
  } else {
    console.warn(`Invalid time format in series ${series.name}:`, point.x)
    return { x: null, y: point.y || 0 }
  }
} else {
  date = dayjs(point.x)
}

          if (!date.isValid()) {
            console.warn(
              `Invalid date detected in series ${series.name}:`,
              point.x
            )
            return { x: null, y: point.y || 0 }
          }
          return { x: date.valueOf(), y: point.y || 0 }
        })
        .filter((point) => point.x !== null)

      return {
        name: series.name,
        color: series.color,
        data: validatedData,
        unit: series.unit,
        yAxisId: series.yAxisId,
      }
    })
  }, [validMapInverterSeries])

  const chartSeries = useMemo(() => {
    const series = processedData.map((series) => ({
      ...series,
      ...(hasDualAxes && {
        yAxisIndex: ['kWh', 'kW'].includes(series.unit) ? 0 : 1,
      }),
    }))

    console.log('Chart series:', series)
    return series
  }, [processedData, hasDualAxes])

  //dynamically change format for the graph
  const getXAxisFormatter = () => {
    if (inverterviewbutton === 'day') {
      return (value) => {
        const date = dayjs(value)
        return date.format('HH:mm')
      }
    } else if (['MTD', '1M', '3M'].includes(inverterviewbutton)) {
      return (value) => {
        const date = dayjs(value)
        return date.isValid() ? date.format('DD-MM-YYYY') : 'Invalid Date'
      }
    } else if (['6M', '1Y', 'YTD'].includes(inverterviewbutton)) {
      return (value) => {
        const date = dayjs(value)
        return date.isValid() ? date.format('MM-YYYY') : 'Invalid Date'
      }
    } else if (inverterviewbutton === 'custom') {
      const fromDate = dayjs(technicalFromDate)
      const toDate = dayjs(technicalToDate)
      const diffInHours = toDate.diff(fromDate, 'hour')
      if (diffInHours <= 24) {
        return (value) => {
          const date = dayjs(value)
          return date.isValid() ? date.format('HH:mm') : 'Invalid Date'
        }
      } else {
        return (value) => {
          const date = dayjs(value)
          return date.isValid() ? date.format('DD-MM-YYYY') : 'Invalid Date'
        }
      }
    }
    return (value) => {
      const date = dayjs(value)
      return date.isValid() ? date.format('MM-YYYY') : 'Invalid Date'
    }
  }

  const chartOptions = useMemo(() => {
    const options = {
      chart: {
        height: 350,
        type: 'line',
        zoom: { enabled: true },
        toolbar: {
          tools: {
            download: true,
            selection: true,
            zoom: true,
            zoomin: true,
            zoomout: true,
            pan: true,
            reset: true,
          },
        },
      },
      legend: { show: true },
      dataLabels: { enabled: false },
      stroke: {
        curve: 'smooth',
        width: 4,
      },
      title: {
        align: 'center',
      },
      markers: { size: 4 },
      grid: {
        row: {
          colors: ['#f3f3f3', 'transparent'],
          opacity: 0.5,
        },
      },
      xaxis: {
        type: 'category',
        labels: {
          rotate: -45,
          rotateAlways: true,
          style: { fontSize: '11px' },
          formatter: getXAxisFormatter(),
        },
        tickPlacement: 'on',
      },
      tooltip: {
        shared: true,
        y: {
          formatter: function (value, { seriesIndex }) {
            const unit = chartSeries[seriesIndex]?.unit || yaxisUnit
            return `${value?.toFixed(2)} ${unit}`
          },
        },
      },
      noData: {
        text: 'No data available',
        align: 'center',
        verticalAlign: 'middle',
        offsetX: 0,
        offsetY: 0,
        style: {
          color: '#000000',
          fontSize: '14px',
          fontFamily: undefined,
        },
      },
    }

    if (hasDualAxes) {
      options.yaxis = [
        {
          seriesName: 'Energy',
          title: {
            text: 'Energy (kWh/kW)',
          },
          labels: {
            formatter: (value) => value?.toFixed(2) || 0,
          },
          min: 0, // Ensure energy starts from 0
        },
        {
          opposite: true,
          seriesName: 'Irradiance',
          title: {
            text: 'Irradiance (kW/m²)',
          },
          labels: {
            formatter: (value) => value?.toFixed(2) || 0,
          },
        },
      ]
    } else {
      options.yaxis = {
        labels: {
          formatter: (value) => value?.toFixed(2) || 0,
        },
        title: {
          text: yaxisUnit,
        },
        min: validMapInverterSeries.some((s) => s.unit === 'kWh')
          ? 0
          : undefined, // Start from 0 for energy data
      }
    }

    return options
  }, [hasDualAxes, yaxisUnit, chartSeries, validMapInverterSeries])

  const hasValidTableData = useMemo(() => {
    return (
      processedData &&
      processedData.length > 0 &&
      processedData[0]?.data &&
      processedData[0].data.length > 0
    )
  }, [processedData])

  console.log('Final processed data:', processedData)
  console.log('Has valid table data:', hasValidTableData)

  return (
    <div>
      {openTable && hasValidTableData ? (
        <div style={{ maxHeight: '360px', overflow: 'auto' }}>
          <TableContainer component={Paper}>
            <Table aria-label="simple table">
              <TableHead>
                <TableRow>
                  <TableCell>Date/Time</TableCell>
                  {dynamicHeadings?.map((heading, index) => (
                    <TableCell key={index}>{heading}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {processedData[0].data.map((row, rowIndex) => (
                  <TableRow key={rowIndex}>
                    <TableCell>
                      {dayjs(row.x).isValid() ? dayjs(row.x).format(
                        inverterviewbutton === 'day'
                          ? 'DD-MM-YYYY HH:mm:ss'
                          : ['1M', 'MTD', '3M'].includes(inverterviewbutton)
                          ? 'DD-MM-YYYY'
                          : ['6M', '1Y', 'YTD'].includes(inverterviewbutton)
                          ? 'MM-YYYY'
                          : 'DD-MM-YYYY HH:mm:ss'
                      ) : 'Invalid Date'}
                    </TableCell>
                    {processedData.map((item, index) => (
                      <TableCell key={index}>
                        {item.data[rowIndex]?.y !== undefined
                          ? `${item.data[rowIndex].y.toFixed(2)} ${
                              item.unit || yaxisUnit
                            }`
                          : `0.00 ${item.unit || yaxisUnit}`}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      ) : (
        <div>
          <div
            style={{ color: '#5968B7', fontSize: '12px', marginLeft: '5px' }}
          >
            {selectedButtons?.length > 1 ? 'Multiple Units' : yaxisUnit}
          </div>
          <div id="chart">
            {processedData && processedData.length > 0 ? (
              <ReactApexChart
                options={chartOptions}
                series={processedData}
                type="line"
                height={360}
              />
            ) : (
              <div
                style={{
                  height: '350px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  fontSize: '16px',
                  color: '#666',
                }}
              >
                No data available for the selected period
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default TechnicalGraphContainer